/**
 * Operation Reference ID Registry
 *
 * Maps (resource_type, action_name) pairs to canonical operation_ref_id values
 * that align with PermissionGrant verb coordinates in Neo4j.
 *
 * These MUST stay in sync with the reconciler mapping in
 * membership/src/storage/graph_modules/graph_authz_reconciler.py
 *
 * Format: operation_ref_id always starts with "op:" prefix
 * Pattern: op:{application}:{noun}.{verb}
 *
 * See AUTHZ_SCHEMA_CANON_AND_DEPRECATIONS.md § PermissionGrant and
 * AUTHZ_V4_APP_ROLE_AND_GRANT_OPERATIONS_GUIDE.md § 9.4
 */

export interface AuthZENAction {
  name: string;
  properties?: { operation_ref_id: string };
}

/**
 * Explicit registry for experience-shell resource types.
 * Plugin-specific resources (e.g. "entitlement-ledger") are NOT listed here;
 * they use the deterministic fallback op:{resource}:{noun}.{verb}.
 */
const OPERATION_REF_MAP: Record<string, Record<string, string>> = {
  'experience.plugin': {
    access: 'op:experience:plugin.access',
    view: 'op:experience:plugin.access',
    configure: 'op:experience:plugin.configure',
    manage_access: 'op:experience:plugin.manage_access',
  },
  'experience.route': {
    view: 'op:experience:route.view',
    navigate: 'op:experience:route.view',
  },
  'experience.tab': {
    view: 'op:experience:tab.view',
  },
  'experience.action': {
    execute: 'op:experience:action.execute',
    view: 'op:experience:action.view',
  },
  'experience:theme-picker': {
    'change-theme': 'op:experience:theme-picker.change',
  },
  // System Editor in-plugin action controls. These resource types carry the
  // sub-action in the third segment (e.g. "system-editor:system:edit"); they
  // map to canonical op:system-editor:<resource>.<action> coordinates that
  // match the bootstrap PermissionGrants and the principal policy.
  // MUST stay in sync with:
  //   ServiceConfigs/pdp/config/applications/system-editor.yaml (api_operations)
  //   ServiceConfigs/membership/config/bootstrap.yaml (permission_grants)
  'system-editor:system:create': { create: 'op:system-editor:system.create' },
  'system-editor:system:edit': { edit: 'op:system-editor:system.edit' },
  'system-editor:system:delete': { delete: 'op:system-editor:system.delete' },
  'system-editor:system:test': { execute: 'op:system-editor:system.test' },
  'system-editor:type:view': { view: 'op:system-editor:type.view' },
  'system-editor:type:edit': { edit: 'op:system-editor:type.edit' },
  'system-editor:command:create': { create: 'op:system-editor:command.create' },
  'system-editor:command:edit': { edit: 'op:system-editor:command.edit' },
  'system-editor:command:delete': { delete: 'op:system-editor:command.delete' },
  'system-editor:command:test': { execute: 'op:system-editor:command.test' },
  'system-editor:schema:edit': { edit: 'op:system-editor:schema.edit' },
  'system-editor:api-import': { create: 'op:system-editor:api-import.create' },
  'system-editor:yaml-editor': { execute: 'op:system-editor:yaml-editor.execute' },
  'system-editor:bulk-operations': { execute: 'op:system-editor:bulk-operations.execute' },
  // Routes Admin plugin resource types. The deterministic fallback would
  // synthesize the wrong noun ("routes_config") from the "routes:config"
  // resource type, so these must be declared explicitly to match the contract
  // and bootstrap PermissionGrants (op noun is "route", not "routes_config").
  // MUST stay in sync with:
  //   ServiceConfigs/pdp/config/applications/routes-admin.yaml (api_operations)
  //   ServiceConfigs/membership/config/bootstrap.yaml (permission_grants)
  'routes:config': {
    read: 'op:routes-admin:config.read',
    edit: 'op:routes-admin:route.edit',
    create: 'op:routes-admin:route.create',
    reload: 'op:routes-admin:route.reload',
    validate: 'op:routes-admin:route.validate',
  },
  'routes:bff': {
    view: 'op:routes-admin:route.view',
  },
  pages: {
    view_all: 'op:experience:pages.view_all',
  },
  workflows: {
    view_all: 'op:experience:workflows.view_all',
    execute_quick: 'op:experience:workflows.execute_quick',
  },
  tasks: {
    view_all: 'op:experience:tasks.view_all',
    view_summary: 'op:experience:tasks.view_summary',
  },
  dashboard: {
    view: 'op:experience:dashboard.view',
  },
  home: {
    view: 'op:experience:home.view',
  },
  catalog: {
    view: 'op:experience:catalog.view',
  },
  profile: {
    view: 'op:experience:profile.view',
    edit: 'op:experience:profile.edit',
  },
  theme: {
    view: 'op:experience:theme.view',
    edit: 'op:experience:theme.edit',
  },
  settings: {
    view: 'op:experience:settings.view',
    edit: 'op:experience:settings.edit',
  },
  user: {
    view: 'op:experience:user.view',
    edit: 'op:experience:user.edit',
  },
  'admin-panel': {
    view: 'op:experience:admin_panel.view',
  },
  'plugin.widget': {
    view: 'op:experience:plugin.widget.view',
  },
};

import {
  inferPdpApplication as inferApplicationFromResource,
  normalizeOperationToken,
} from './authz-constants';

const fallbackUsageCount = new Map<string, number>();
const warnedFallbackKeys = new Set<string>();

/**
 * Resolve the canonical operation_ref_id for a given resource type and action.
 *
 * For known resource/action pairs, uses the explicit registry.
 * For unknown pairs, produces a deterministic fallback using the correct
 * application prefix: op:{application}:{noun}.{verb}
 *
 * Per AUTHZ_SCHEMA_CANON § PermissionGrant, the application prefix comes
 * from the resource's pdp_application, NOT always "experience".
 */
export function resolveOperationRefId(
  resourceType: string,
  actionName: string,
): string | undefined {
  const explicit = OPERATION_REF_MAP[resourceType]?.[actionName];
  if (explicit) {
    return explicit;
  }

  const noun = normalizeOperationNoun(resourceType);
  const verb = normalizeOperationVerb(actionName);
  if (!noun || !verb) {
    return undefined;
  }
  const app = inferApplicationFromResource(resourceType);
  recordFallbackUsage(resourceType, actionName);
  return `op:${app}:${noun}.${verb}`;
}

/**
 * Check whether a resource type participates in the verb+noun grant model.
 */
export function isGrantAwareResourceType(resourceType: string): boolean {
  return resourceType in OPERATION_REF_MAP;
}

/**
 * Build a typed AuthZEN action object with operation_ref_id.
 *
 * NOTE: Per AUTHZ-01, operation_ref_id is MANDATORY. This function always
 * produces a valid op_ref, falling back to a deterministic canonical form
 * if no explicit mapping exists. Never returns an action without
 * operation_ref_id - that would cause PDP hard contract violations.
 */
export interface PluginRouteAuthZInput {
  path: string;
  action?: string;
  op_ref_id?: string;
  resource_id?: string;
}

export interface PluginRouteAuthZEvaluation {
  resource: {
    type: 'experience.route';
    id: string;
    properties: { pdp_application: string };
  };
  action: AuthZENAction;
  context: Record<string, unknown>;
}

function normalizePluginRoutePath(path: string): string {
  const raw = (path || '').trim();
  if (!raw) return '/';
  return raw.startsWith('/') ? raw : `/${raw}`;
}

/**
 * Build a PDP evaluation payload for a plugin manifest route.
 * Uses manifest op_ref_id / resource_id when present so PermissionGrant
 * coordinates match bootstrap (e.g. op:identity-core:plugin-route.view).
 */
export function buildPluginRouteEvaluation(
  pluginId: string,
  routePdpApp: string,
  route: PluginRouteAuthZInput,
  baseContext: Record<string, unknown> = {},
): PluginRouteAuthZEvaluation {
  const actionName = route.action || 'view';
  const action = route.op_ref_id
    ? { name: actionName, properties: { operation_ref_id: route.op_ref_id } }
    : buildAuthZENAction('experience.route', actionName);
  const resourceId =
    route.resource_id || `route:${pluginId}:${normalizePluginRoutePath(route.path)}`;

  return {
    resource: {
      type: 'experience.route',
      id: resourceId,
      properties: { pdp_application: routePdpApp },
    },
    action,
    context: { ...baseContext, pdp_application: routePdpApp },
  };
}

export function buildAuthZENAction(
  resourceType: string,
  actionName: string,
): AuthZENAction {
  let opRef = resolveOperationRefId(resourceType, actionName);

  if (!opRef) {
    const app = inferApplicationFromResource(resourceType);
    const noun = normalizeOperationNoun(resourceType) || resourceType.trim().replace(/[^a-zA-Z0-9._-]/g, '_');
    const verb = normalizeOperationVerb(actionName) || actionName.trim().replace(/[^a-zA-Z0-9._-]/g, '_');
    opRef = `op:${app}:${noun || 'unknown'}.${verb || 'execute'}`;

    console.warn(
      '[AuthZ OperationRefs] Forced fallback operation_ref_id. Add explicit registry entry.',
      { resourceType, actionName, fallbackOpRef: opRef }
    );
  }

  return {
    name: actionName,
    properties: { operation_ref_id: opRef },
  };
}

/**
 * Read-only snapshot of fallback usage counts keyed by "resourceType:actionName".
 */
export function getOperationRefFallbackUsageSnapshot(): Record<string, number> {
  return Object.fromEntries(fallbackUsageCount.entries());
}

/**
 * Test helper to reset fallback usage state.
 */
export function _resetOperationRefFallbackUsageForTests(): void {
  fallbackUsageCount.clear();
  warnedFallbackKeys.clear();
}

function normalizeOperationNoun(resourceType: string): string {
  const trimmed = resourceType.trim();
  const withoutPrefix = trimmed
    .replace(/^experience:/i, '')
    .replace(/^experience\./i, '');
  return normalizeOperationToken(withoutPrefix);
}

function normalizeOperationVerb(actionName: string): string {
  return normalizeOperationToken(actionName);
}

// normalizeOperationToken imported from authz-constants.ts

function recordFallbackUsage(resourceType: string, actionName: string): void {
  const key = `${resourceType}:${actionName}`;
  fallbackUsageCount.set(key, (fallbackUsageCount.get(key) ?? 0) + 1);
  if (!warnedFallbackKeys.has(key)) {
    warnedFallbackKeys.add(key);
    console.warn(
      '[AuthZ OperationRefs] Using fallback operation_ref_id mapping. Add explicit registry entry.',
      { resourceType, actionName, fallbackKey: key },
    );
  }
}

// Publish the registry resolver onto globalThis.__authzConstants so the
// externalized @experience/plugin-auth SDK (a separate bundle) can resolve
// canonical operation_ref_id values instead of synthesizing a fallback from
// the raw resource type. authz-constants.ts evaluates first (this module
// imports from it), so the object already exists; we augment it in place.
// This module is loaded at startup via authz-preload → AuthZENInitializer.
{
  const authzConstants = (globalThis as any).__authzConstants || {};
  authzConstants.resolveOperationRefId = resolveOperationRefId;
  authzConstants.isGrantAwareResourceType = isGrantAwareResourceType;
  (globalThis as any).__authzConstants = authzConstants;
}
